import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Typography } from '@material-ui/core';
import Accordion from '@material-ui/core/ExpansionPanel';
import AccordionDetails from '@material-ui/core/ExpansionPanelDetails';
import AccordionSummary from '@material-ui/core/ExpansionPanelSummary';
import cx from 'classnames';
import { useObserver } from 'mobx-react';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FBSchemaProps, FBStarter } from '..';
import { ReactComponent as CollapseMoreIcon } from '../../../assets/images/CollapseMoreIcon.svg';
import { ReactComponent as ExpandMoreIconCustom } from '../../../assets/images/ExpandMoreIcon.svg';
import { translate } from '../../../common/intl';
import { documentRevisionsActions, documentRevisionsSelectors } from '../../../state/ducks/documentRevisions';
import { ApplicationState } from '../../../state/reducers';
import FBDeviationMpiComponent from './FBDeviationMpiComponent';
import FBDeviationProcedureState from './FBDeviationProcedure.state';
import useDeviationProcedureStyles from './FBDeviationProcedure.styles';
import FBDeviationProcedureStep from './FBDeviationProcedureStep';
import { FBDeviationStepValue, isDeviationProcedureStep, isDeviationReadOnlyField } from './FBDeviationProcedure.types';

// One added component in the drag list (same transform handling as FBSortable's SortableItem)
const SortableBox: React.FC<{ id: string }> = ({ id, children }) => {
  const { setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const classes = useDeviationProcedureStyles();
  return (
    <Box
      {...{ ref: setNodeRef }}
      mb={2}
      className={cx(classes.sortableItem, { [classes.sortableItemDragging]: isDragging })}
      // transform is per frame while dragging, so it can't be a class
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      {children}
    </Box>
  );
};

interface MpiSectionProps {
  mpiId: string
  mpiIndex: number
  steps: Record<string, FBDeviationStepValue>
  name: string
  disabled?: boolean
  isPreview?: boolean
  isOutput?: boolean
  canEdit?: boolean
  deviationProcedureState?: FBDeviationProcedureState
  // owned by FBDeviationProcedure so "Expand All" can open every section
  expanded: boolean
  onToggle: () => void
}

const FBDeviationMpiSection: React.FC<MpiSectionProps> = ({
  mpiId, mpiIndex, steps, name, disabled, isPreview, isOutput, canEdit, deviationProcedureState, expanded, onToggle,
}) => {
  const classes = useDeviationProcedureStyles();
  // jsonb does not keep key order: restore the MPI's component order
  const stepEntries = Object.entries(steps).sort(
    ([, a], [, b]) => ((a.schemaItem?.index as number) ?? 1e6) - ((b.schemaItem?.index as number) ?? 1e6),
  );
  // Number of sequences (procedure steps) in the MPI, as in Figma ("4 Steps")
  const sequenceCount = stepEntries.filter(([, it]) => isDeviationProcedureStep(it)).length;
  // The MPI's own schema (nested names, no tabs) so index-based controls resolve against it, not the deviation's schema
  const mpiSchema = React.useMemo(() => stepEntries.map(([stepId, step], index) => ({
    ...step.schemaItem,
    type: step.type,
    label: step.label,
    name: `${name}.${mpiId}.${stepId}`,
    tabId: undefined,
    index,
  })) as FBSchemaProps[],
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [mpiId, name,
    stepEntries.map(([id]) => id).join(), // sorted order: it changes when a component is moved
    // an edited added component keeps its key and position, so track its definition too (not `steps`:
    // that changes on every keystroke in a procedure description)
    JSON.stringify(stepEntries.filter(([, it]) => it.added).map(([, it]) => it.schemaItem))]);

  // Added components, in the order the user sees them; they are reordered by dragging the field toolbar's handle
  const visible = stepEntries.filter(([, it]) => it.added && !it.schemaItem?.hidden);
  const sortableId = (stepId: string) => `${name}.${mpiId}.${stepId}`; // the toolbar's useSortable id (field name)
  const sensors = useSensors(useSensor(PointerSensor));
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    const from = visible.findIndex(([id]) => sortableId(id) === active.id);
    const to = visible.findIndex(([id]) => sortableId(id) === over?.id);
    if (from < 0 || to < 0 || from === to) { return; }
    const dir = to > from ? 1 : -1;
    const idx = (i: number) => Number(visible[i]?.[1].schemaItem?.index) || 0;
    // land between the drop target and the one after it (or just past the target at either end)
    const next = visible[to + dir] ? (idx(to) + idx(to + dir)) / 2 : idx(to) + dir;
    deviationProcedureState?.setComponentIndex(mpiId, visible[from][0], next);
  };

  const mpiRevId = mpiId.split(':')[0]; // key is <mpiId>:<stepIndex>
  const docRevision = useSelector((state: ApplicationState) =>
    documentRevisionsSelectors.getDocumentRevision(state, mpiRevId),
  );
  const dispatch = useDispatch();
  // The MPI is usually not in the store on the Deviation page: load it for the "(<docId> - <name>)" label.
  // reload, not load: load's async type makes the page's AsyncGate show a spinner over the deviation.
  const isMpiLoaded = Boolean(docRevision);
  useEffect(() => {
    if (!isMpiLoaded) { dispatch(documentRevisionsActions.reload(mpiRevId)); }
  }, [dispatch, mpiRevId, isMpiLoaded]);

  const docId = docRevision?.document?.docId;
  const docName = docRevision?.name;
  const mpiLabel = docId && docName ? `${docId} - ${docName}` : (docId ?? docName);

  // Use mpiIndex from step data (all steps in same MPI share the same mpiIndex)
  const lhrStepIndex = stepEntries[0]?.[1]?.mpiIndex ?? mpiIndex;
  const stepLabel = translate('lhrDeviation.step.label', { number: lhrStepIndex });

  const isDeviated = useObserver(() => deviationProcedureState?.deviatedMpiIds.has(mpiId) ?? false);

  return (
    <Accordion
      className={classes.panelBorder}
      expanded={expanded}
      onChange={onToggle}
    >
      <AccordionSummary
        className={classes.panelSummary}
        expandIcon={expanded ? <ExpandMoreIconCustom /> : <CollapseMoreIcon />}
      >
        <Box className={cx(classes.labelContainer, { [classes.headerExpanded]: expanded })}>
          <Box display="flex" alignItems="center" flexGrow={1} overflow="hidden">
            <Typography variant="subtitle2" className={classes.stepLabelText}>
              {stepLabel}
            </Typography>
            {mpiLabel && (
              <Typography
                variant="subtitle2"
                className={classes.mpiLabelText}
              >
                {`(${mpiLabel})`}
              </Typography>
            )}
            {isDeviated && (
              <Typography className={classes.deviatedBadge}>
                {translate('lhrDeviation.badge.deviated')}
              </Typography>
            )}
          </Box>
          <Typography className={classes.stepCountLabel}>
            {translate('lhrDeviation.step.count', { count: sequenceCount })}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext items={visible.map(([stepId]) => sortableId(stepId))} strategy={verticalListSortingStrategy}>
            {stepEntries.map(([stepId, stepData], position) => {
              if (!isDeviationProcedureStep(stepData)) {
                // Cloned MPI components stay in the schema (the LHR step needs them) but are not shown here,
                // except the step quantities, which are shown read-only (they are entered during LHR execution)
                if (!stepData.added) {
                  if (!isDeviationReadOnlyField(stepId)) { return null; }
                  return (
                    <Box key={stepId} mb={2}>
                      <FBDeviationMpiComponent {...{ mpiId, stepId, stepData, name, mpiSchema, position }} canEdit={false} />
                    </Box>
                  );
                }
                return (
                  <SortableBox key={stepId} id={sortableId(stepId)}>
                    <FBDeviationMpiComponent
                      {...{ mpiId, stepId, stepData, name, mpiSchema, position, canEdit }}
                      onRemove={() => deviationProcedureState?.removeComponent(mpiId, stepId)}
                      onEdit={(item) => deviationProcedureState?.editComponent(mpiId, stepId, item)}
                    />
                  </SortableBox>
                );
              }
              const stepState = deviationProcedureState?.getStepState(mpiId, stepId);
              if (!stepState) { return null; }

              return (
                <FBDeviationProcedureStep
                  key={stepId}
                  {...{ mpiId, stepId, stepData, name, disabled, isPreview, isOutput, isDeviated }}
                  mpiProcedureState={stepState}
                />
              );
            })}
          </SortableContext>
        </DndContext>
        {canEdit && (
          <Box
            mt={1}
            onClickCapture={() => {
              FBDeviationProcedureState.onSave = (item) => deviationProcedureState?.addComponent(mpiId, item);
            }}
          >
            <FBStarter index={0} />
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default FBDeviationMpiSection;
